import { latLngBounds, LatLngExpression } from 'leaflet';
import { getCombinedLevelItemBounds } from '../utils/utils';
import L from 'leaflet';
import { MapStore } from 'stores/MapStore';
import { User } from 'types/userInfo';
import { Asset } from 'types/asset';
import { useContext } from 'react';
import { MapContainerRefContext } from '../utils/mapContainerRef.context';
import { Device } from 'types/device';

export default function useMap(mapStore: MapStore) {
  const { map } = useContext(MapContainerRefContext)
  const {
    assets, outdoorAssets,
    users, outdoorUsers,
    devices, outdoorDevices,
    levels, getBuildingLevels,
    selectedBuildingId,
    selectBuilding, selectFloor, getFirstNotEmptyFloor
  } = mapStore

  function isMarkerVisible(obj: Asset | User | Device) {
    if (!map)
      return false
    const bounds = map.getBounds();
    const lat = obj.location?.coordinates.latitude;
    const lng = obj.location?.coordinates.longitude;
    return lat && lng && bounds.contains(L.latLng(lat, lng));
  }

  function fitMapToBoundsOfGeoJSON(geoJSONData: any, maxZoom: number = 21) {
    if (!map)
      return
    const geojsonLayer = L.geoJSON(geoJSONData);
    map.fitBounds(geojsonLayer.getBounds(), { padding: [50, 50], maxZoom: maxZoom });
  }

  function panToLatLong(eTargetLatLong: LatLngExpression) {
    if (!map)
      return
    map.panTo(eTargetLatLong);
  }

  function fitMapToFilterResults(map: L.Map, items: Asset[] | User[] | Device[]) {
    const bounds = latLngBounds([]);
    const buildings: string[] = []

    if (items.length === 1) {
      if (selectedBuildingId === null && items[0].location.building_level?.possible_buildings[0].id) {
        selectBuilding(items[0].location.building_level.possible_buildings[0].id)
      }
      if (selectedBuildingId && selectedBuildingId === items[0].location.building_level?.possible_buildings[0].id) {
        selectFloor(getFirstNotEmptyFloor(selectedBuildingId))
      }
    }

    //show all buildings with searched items and also outdoor items
    for (const item of items) {
      if (item.location.is_outdoors)
        bounds.extend([item.location.coordinates.latitude, item.location.coordinates.longitude])
      else {
        item.location?.building_level?.possible_buildings?.forEach(building => {
          if (buildings.includes(building.id))
            return
          buildings.push(building.id)
          const buildingLevels = getBuildingLevels(building.id)
          const geoJSONLayer = L.geoJSON(buildingLevels as any);
          bounds.extend(geoJSONLayer.getBounds());
        })
      }
    }

    if (selectedBuildingId) {
      buildings.push(selectedBuildingId)
      const buildingLevels = getBuildingLevels(selectedBuildingId)
      const geoJSONLayer = L.geoJSON(buildingLevels as any);
      bounds.extend(geoJSONLayer.getBounds());
    }

    if (buildings.length === 0) {
      const geoJSONLayer = L.geoJSON(levels as any);
      bounds.extend(geoJSONLayer.getBounds());
    }

    map.fitBounds(bounds);
  }

  function panMapToShowAll(map: L.Map) {
    const combinedAssetBounds = [...assets, ...outdoorAssets];
    const combinedUserBounds = [...users, ...outdoorUsers];
    const combinedDeviceBounds = [...devices, ...outdoorDevices];
    const bounds = getCombinedLevelItemBounds(
      combinedAssetBounds,
      combinedUserBounds,
      combinedDeviceBounds,
      levels,
    );
    map.fitBounds(bounds, { animate: true });
  }

  return {
    fitMapToBoundsOfGeoJSON,
    panToLatLong,
    fitMapToFilterResults,
    panMapToShowAll,
    isMarkerVisible
  };
}
